<script setup lang="ts">
import type Carousel from '@/components/ui/carousel/Carousel.vue';
import type {
	HighlightedRegionPropertiesSectionEmits,
	HighlightedRegionPropertiesSectionProps
} from '.';

import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { PropertyCard } from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { CarouselGenericAction } from '@/components/ui/carousel/CarouselGenericAction';
import { CarouselItemSelector } from '@/components/ui/carousel/CarouselItemSelector';
import { cn } from '@/utils';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import messages from './HighlightedRegionPropertiesDetailsSection.locale.json';

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(
	defineProps<HighlightedRegionPropertiesSectionProps>(),
	{
		containerClass: '',
		buttonVariant: 'primary',
		buttonClass: '',
		sectionName: 'similar properties',
		carouselItemClass: 'sm:basis-[49%]',
		carouselClass: 'max-w-5xl'
	}
);

const emit = defineEmits<HighlightedRegionPropertiesSectionEmits>();

const title = computed<string>(() => {
	if (props.title?.trim()) return props.title;

	return t('title', { region: props.region });
});

// Constants
const SLIDE_DELAY = 2000;

// Refs and States
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);
const propertyHref = computed<string>(
	() =>
		props.buttonHref ||
		PropertyFiltersPresenter.getPathFromFilters({
			regions: [props.region],
			propertyTypes: [props.propertyType] as PropertyTypeEnum[],
			cities: props.city ? [props.city] : [],
			states: props.state ? [props.state] : []
		})
);

const paddingCarouselItem = (index: Number) => {
	if (index === 0) return 'pl-4 md:!px-0';
	if (index === props.properties.length - 1) return 'pr-4 md:!px-0';
	return '';
};

const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const clickCount = ref(0);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: true
});

const ctaText = computed<string>(() => props.buttonText || t('buttonText'));

function handleViewMoreClicked(navigate: () => void, href: string) {
	clickCount.value++;
	emit('cta-view-more-clicked', {
		destination: href,
		clickCount: clickCount.value,
		ctaText: ctaText.value
	});
	navigate();
}

const getSkeletonClass = computed<string>(() =>
	props.isLoading ? 'h-7 w-1/2 md:w-[359px]' : ''
);

const showSliderSelector = computed<boolean>(
	() => scrollableSlidesNumber.value > 1
);
</script>

<template>
	<section class="w-full bg-white">
		<Container
			:class="
				cn(
					'!max-w-full flex-1 overflow-hidden py-8 md:py-[60px]',
					props.containerClass
				)
			"
		>
			<header class="flex flex-col items-start gap-4 pb-6 md:pb-9">
				<Skeleton
					:is-active="isLoading"
					variant="white"
					:class="{ 'h-[34px] w-1/2 md:w-[359px]': isLoading }"
				>
					<h2
						:class="
							cn(
								'flex items-center font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-4xl md:tracking-[-0.045rem]',
								titleClass
							)
						"
					>
						{{ title }}
					</h2>
				</Skeleton>
			</header>

			<div :class="cn('flex w-full flex-col gap-6 md:gap-9', carouselClass)">
				<div v-if="isLoading" class="flex items-center gap-5">
					<PropertyCardSkeleton
						v-for="index in 2"
						:key="`highlighted-property-skeleton-${index}`"
						variant="whiteBackground"
					/>
				</div>

				<Carousel
					v-else
					ref="carouselContainerRef"
					class="w-full"
					:class="carouselClass"
					:plugins="[WheelGesturesPlugin()]"
					:opts="{ align: 'start' }"
				>
					<CarouselContent class="max-w-full gap-5">
						<CarouselItem
							v-for="(property, index) in properties"
							:key="property.commercialId"
							:class="
								cn(
									'min-h-fit basis-11/12 sm:basis-full',
									carouselItemClass,
									paddingCarouselItem(index)
								)
							"
						>
							<NuxtLink
								:to="`/imovel/${property.commercialId}/${property.slug}`"
								target="_blank"
								rel="noopener noreferrer"
							>
								<PropertyCard
									variant="whiteBackground"
									:section="sectionName"
									:property="property"
									:card-position="index"
								/>
							</NuxtLink>
						</CarouselItem>
					</CarouselContent>
				</Carousel>
				<div
					class="flex h-auto w-full flex-col-reverse items-start justify-between gap-6 md:mx-0 lg:flex-row lg:items-center"
				>
					<NuxtLink v-slot="{ href, navigate }" custom :to="propertyHref">
						<Button
							as="a"
							:variant="buttonVariant"
							:class="
								cn(
									'w-min self-start px-4 py-3 underline decoration-white-dark-4 underline-offset-[6px] transition hover:decoration-primary md:px-5 md:py-[11px]',
									buttonClass
								)
							"
							:href="href"
							:is-skeleton-loading="isLoading"
							skeleton-classes="w-[231px] md:w-[290px] h-[52px]"
							skeleton-variant="white"
							@click="handleViewMoreClicked(navigate, href)"
						>
							{{ ctaText }}
							<IconMaterialSymbolsNorthEast class="w-4 md:w-5" />
						</Button>
					</NuxtLink>

					<Skeleton
						:is-active="isLoading"
						variant="white"
						:class="cn(getSkeletonClass, 'w-full')"
					>
						<div
							v-if="showSliderSelector"
							class="flex w-full items-center gap-3"
						>
							<div
								ref="carouselItemSelectorContainerRef"
								:class="
									cn(
										'flex w-full flex-wrap justify-end gap-2 md:gap-3',
										carouselItemSelectorClass
									)
								"
							>
								<CarouselItemSelector
									v-for="(_, index) in scrollableSlidesNumber"
									:key="`selector-${index}`"
									:active="currentCarouselItemIndex === index"
									:delay="SLIDE_DELAY"
									:dynamic-width="carouselDynamicItemWidth"
									variant="dark"
									size="sm"
									class="md:max-h-[380px] md:max-w-[464px]"
									@click="scrollTo(index)"
								/>
							</div>
							<div class="flex max-w-[66px] gap-[10px] md:gap-2">
								<CarouselGenericAction
									variant="dark"
									@click="scrollTo(currentCarouselItemIndex - 1)"
								>
									<IconMaterialSymbolsLightChevronLeft
										width="28px"
										height="28px"
									/>
								</CarouselGenericAction>
								<CarouselGenericAction variant="dark">
									<IconMaterialSymbolsLightChevronRight
										width="28px"
										height="28px"
										@click="scrollTo(currentCarouselItemIndex + 1)"
									/>
								</CarouselGenericAction>
							</div>
						</div>
					</Skeleton>
				</div>
			</div>
		</Container>
	</section>
</template>
